import { queryClient } from "helpers/queryClient";

import { APIHandler } from "utilities/apiHandler";

import { FilterEntity, FilterEntityType } from "./Filter.api.types";

export interface IFilterHandler {
    get(id: FilterEntityType, isBTAdmin?: boolean, jobID?: number | null): Promise<FilterEntity>;
}

// Filter types whose response is safe to cache across in-list navigation.
//
// jobId is intentionally omitted from the query key (server does not build from the jobsite),
// so switching jobs reuses the cached response. Current consumers are apply-only; a future page
// that adds a type here AND lets users save filters must evict the ["filters", ...] queries after
// a save (queryClient.removeQueries), otherwise the next load serves a stale cached entity.
const cacheableFilterTypes = new Set<FilterEntityType>([FilterEntityType.MediaFolderDetails]);

const filterCacheMs = 5 * 60 * 1000;

const buildFilterQueryKey = (id: FilterEntityType, isBTAdmin?: boolean) =>
    ["filters", id, isBTAdmin ?? false] as const;

function fetchRawFilters(
    id: FilterEntityType,
    isBTAdmin?: boolean,
    jobID?: number | null,
): Promise<object> {
    const data = {
        jobID: jobID,
        useSession: jobID === undefined,
    };
    return APIHandler(`/api/Filters/${id}`, {
        method: "GET",
        // Return the raw payload untouched so it can be cached and hydrated into
        // a fresh FilterEntity per call.
        responseType: (raw: object): object => raw,
        isBTAdmin,
        data: data,
    });
}

export class FilterHandler implements IFilterHandler {
    async get(
        id: FilterEntityType,
        isBTAdmin?: boolean,
        jobID?: number | null,
    ): Promise<FilterEntity> {
        // Cache the raw response and hydrate a fresh entity per call, so
        // per-call mutations by callers don't leak across cache hits.
        const raw = cacheableFilterTypes.has(id)
            ? await queryClient.fetchQuery({
                  queryKey: buildFilterQueryKey(id, isBTAdmin),
                  queryFn: () => fetchRawFilters(id, isBTAdmin, jobID),
                  staleTime: filterCacheMs,
                  cacheTime: filterCacheMs,
                  retry: false,
              })
            : await fetchRawFilters(id, isBTAdmin, jobID);

        return new FilterEntity(raw);
    }
}

export const defaultFilterHandler = new FilterHandler();
